import {
  type CensusQuickBooksResult,
  authorizeCensusQuickBooks,
  getCensusQuickBooksResult,
} from "@/api/census-quickbooks-client";
import {
  type CensusConnectStep,
  type UseCensusHrConnectReturn,
  useCensusHrConnect,
} from "@/hooks/use-census-hr-connect";

/**
 * Drives the company-census "Connect QuickBooks Payroll" step.
 *
 * Diverges from the platforms-demo (which shows a mocked connection modal): the
 * CTA opens the REAL Intuit OAuth popup immediately. The popup/postMessage state
 * machine lives in `use-census-hr-connect` and is shared with BambooHR — see
 * that file for the flow and for why the popup CLOSING (not its postMessage) is
 * the reliable trigger.
 */

/** Retained name — the shared machine's step type. */
export type CensusQBStep = CensusConnectStep;

interface UseCensusQuickBooksOptions {
  /** Called once the pull has been fetched and mapped. */
  onConnected: (result: CensusQuickBooksResult) => void;
  /** Fired on each state transition — used to emit analytics events. */
  onStep?: (step: CensusQBStep, meta?: { error?: string; result?: CensusQuickBooksResult }) => void;
}

export function useCensusQuickBooks(
  opts: UseCensusQuickBooksOptions,
): UseCensusHrConnectReturn<void> {
  return useCensusHrConnect<CensusQuickBooksResult, void>({
    messageType: "quickbooks-oauth",
    authorize: () => authorizeCensusQuickBooks(),
    getResult: () => getCensusQuickBooksResult(),
    onConnected: opts.onConnected,
    onStep: opts.onStep,
    messages: {
      notConnected: "We couldn't read your QuickBooks data. Please try again.",
      resultFailed: "Failed to load your QuickBooks data.",
      authorizeFailed: "Failed to start QuickBooks authorization.",
      timedOut: "QuickBooks didn't finish connecting. Please try again, or add your team manually.",
    },
  });
}
